/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { DatabaseIcon } from "@heroicons/react/outline";
import CircleLoader from "../../../components/loader";
import { useAlert } from "../../../hooks/useAlert";
import { useCSVReader } from "react-papaparse";
import Papa from "papaparse";
import { Parser } from "json2csv";
import Ajv from "ajv";
import axios from "axios";
import { toast } from "sonner";
import { api } from "../../../services/axios";
var uuid = require("uuid");
const ajv = new Ajv(); // options can be passed, e.g. {allErrors: true}

const athleteSchema = {
  type: "object",
  properties: {
    id: { type: "string" },
    account_name: { type: "string" },
    full_name: { type: "string" },
    first_name: { type: "string" },
    last_name: { type: "string" },
    status: { type: "string" },
    number: { type: "string" },
    salesforce_id: { type: "string" },
    org_salesforce_id: { type: "string" },
    college: { type: "string" },
    college_salesforce_id: { type: "string" },
    position: { type: "string" },
    sports: { type: "string" },
    activity: { type: "string" },
    tags: { type: "string" },
    hometown: { type: "string" },
  },
  required: [
    "id",
    "account_name",
    "full_name",
    "first_name",
    "last_name",
    "status",
    "number",
    "salesforce_id",
    "activity",
    "org_salesforce_id",
    "college_salesforce_id",
  ],
  additionalProperties: true,
};

const orgSchema = {
  type: "object",
  properties: {
    default_name: { type: "string" },
    full_name: { type: "string" },
    parents: { type: "string" },
    tags: { type: "string" },
    licensing: { type: "string" },
    motto: { type: "string" },
    acronym_name: { type: "string" },
    founding_year: { type: "string" },
    affinity_organization_id: { type: "string" },
    category: { type: "string" },
    account_name: { type: "string" },
    menu_name: { type: "string" },
    salesforce_id: { type: "string" },
    national: { type: "string" },
    id: { type: "string" },
    colloquial_name: { type: "string" },
    colloquial_phrase: { type: "string" },
    motto_short: { type: "string" },
    motto_long: { type: "string" },
    colloquial_letters: { type: "string" },
    city: { type: "string" },
    state: { type: "string" },
    website: { type: "string" },
  },
  required: ["id", "category", "full_name", "menu_name", "salesforce_id"],
  additionalProperties: true,
};

const collectionSchema = {
  type: "object",
  properties: {
    id: { type: "string" },
    title: { type: "string" },
    shopify_id: { type: "string" },
    store: { type: "string" },
    handle: { type: "string" },
    description: { type: "string" },
    admin_graphql_api_id: { type: "string" },
    salesforce_id: { type: "string" },
    created_at: { type: "string" },
  },
  required: [
    "title",
    "store",
    "handle",
    "description",
    "admin_graphql_api_id",
    "salesforce_id",
  ],
};

const templateSchema = {
  type: "object",
  properties: {
    id: { type: "string" },
    template_count: { type: "number" },
    handle: { type: "string" },
    title: { type: "string" },
    body_html: { type: "string" },
    campaign: { type: "string" },
    organization: { type: "string" },
    file: { type: "string" },
    sku: { type: "string" },
    variants: { type: "array", items: { type: "string" } },
    layers: { type: "array", items: { type: "string" } },
    design_area: { type: "string" },
    licensing: { type: "array", items: { type: "string" } },
    stores: { type: "array", items: { type: "string" } },
    college_names: { type: "array", items: { type: "string" } },
    org_names: { type: "array", items: { type: "string" } },
    org_salesforce_ids: { type: "array", items: { type: "string" } },
    tags: { type: "array", items: { type: "string" } },
    upis: { type: "array", items: { type: "string" } },
    auto_run: { type: "boolean" },
  },
};

export default function ImportDataModal({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const { CSVReader } = useCSVReader();
  const [loading, setLoading] = useState(false);
  const cancelButtonRef = useRef(null);
  const alert = useAlert();

  const [model, setSelectedModel] = useState<string>("athletes");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<null | { message: string }>(null);

  const handleImportData = async () => {
    if (!open) {
      return;
    }
    setLoading(true);
    try {
      Papa.parse(selectedFile as File, {
        header: true,
        worker: true, // Don't bog down the main thread if its a big file
        complete: async function (results, f) {
          const chunks = chunkArrayInGroups(results.data, 1000);
          for await (const chunk of chunks) {
            const json2csvParser = new Parser();
            const csv = json2csvParser.parse(chunk);
            const randomname = uuid.v4();
            var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            var file = new File([blob], `${randomname}.csv`);
            await uploadFile(file, `${model}_${randomname}.csv`);
          }
          setSelectedFile(null);
          setSelectedModel("athletes");

          alert.showAlert(
            "Successfully imported!",
            "Your data was successfully imported it may take a few minutes and a page refresh for the new tables to show up.",
            "Success"
          );

          setLoading(false);
          setOpen(false);
        },
      });
    } catch (e) {
      console.log(e);
      alert.showAlert(
        "Error importing!",
        "There was a problem importing this spreadsheet please try again later",
        "Error"
      );
      setLoading(false);
    }
  };

  function chunkArrayInGroups(arr: any[], size: number) {
    var myArray = [];
    for (var i = 0; i < arr.length; i += size) {
      myArray.push(arr.slice(i, i + size));
    }
    return myArray;
  }

  const uploadFile = async (file: File, filename: string) => {
    try {
      const response = await api.post("/bulk", {
        fileName: filename,
        contentType: "text/*",
      });
      const config = {
        headers: {
          "Content-Type": "text/*",
        },
        onUploadProgress: function (progressEvent: any) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log(percentCompleted);
        },
      };
      return axios
        .put(response.data.url, file, config)
        .then((res) => {
          console.log("url", res.config.url);
        })
        .catch((error: any) => {
          console.log(error);
          alert.showAlert(
            "Error importing!",
            "There was a problem importing this spreadsheet please try again later",
            "Error"
          );
        });
    } catch (e) {
      console.log(e);
      alert.showAlert(
        "Error importing!",
        "There was a problem importing this spreadsheet please try again later",
        "Error"
      );
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0 min-w-fit">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                  <DatabaseIcon
                    className="h-6 w-6 text-indigo-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left mb-3">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    Import data set
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {loading
                        ? "Importing data please do not close this window"
                        : "Please select one file that you would like to upload."}
                    </p>
                  </div>
                  <div className="w-full my-6">
                    <label
                      htmlFor="menu_name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      1. Select a Model
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <select
                        id="model"
                        name="model"
                        autoComplete="model"
                        value={model}
                        onChange={(e) => {
                          setSelectedModel(e.target.value);
                          setSelectedFile(null);
                          setError(null);
                        }}
                        className="w-full border-r border-gray-300 py-2 focus:ring-indigo-500 focus:border-indigo-500 h-full pl-3 pr-7 bg-gray-50 text-gray-500 sm:text-sm rounded-md"
                      >
                        <option value="none" disabled hidden>
                          Select a Model
                        </option>
                        {[
                          "athletes",
                          "organizations",
                          "collections",
                          "designed product templates",
                        ].map(function (item) {
                          return (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <label
                        htmlFor="menu_name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        2. Select a File
                      </label>
                      <CSVReader
                        config={{
                          header: true,
                        }}
                        onUploadAccepted={(results: any, file: File) => {
                          let validate: any;
                          let data: any;

                          console.log("model", model);
                          console.log("data", results.data);

                          switch (model) {
                            case "athletes":
                              data = results.data.map((member: any) => {
                                return {
                                  ...member,
                                  id: member.salesforce_id,
                                };
                              });
                              validate = ajv.compile(athleteSchema);
                              break;
                            case "organizations":
                              data = results.data.map((org: any) => {
                                return {
                                  id: org.salesforce_id,
                                  ...org,
                                };
                              });
                              validate = ajv.compile(orgSchema);
                              break;
                            case "collections":
                              data = results.data;
                              validate = ajv.compile(collectionSchema);
                              break;
                            case "templates":
                              data = results.data;
                              validate = ajv.compile(templateSchema);
                              break;
                            default:
                              console.log(
                                `Sorry, we don't support this model type.`
                              );
                          }

                          data.forEach((item: any, i: number) => {
                            if (!validate(item)) {
                              setError({
                                message: `Invalid file check line: ${i}`,
                              });
                              return;
                            }
                          });

                          setError(null);
                          setSelectedFile(file);
                        }}
                        noDrag
                      >
                        {({ getRootProps }: any) => (
                          <>
                            {error && (
                              <p className="text-red-500">{error?.message}</p>
                            )}
                            {selectedFile && (
                              <p className="text-gray-500">
                                {selectedFile.name}
                              </p>
                            )}
                            {!selectedFile && (
                              <div
                                {...getRootProps()}
                                className="max-w-lg flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md"
                              >
                                <div className="space-y-1 text-center">
                                  <svg
                                    className="mx-auto h-12 w-12 text-gray-400"
                                    stroke="currentColor"
                                    fill="none"
                                    viewBox="0 0 48 48"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  <div className="flex items-center justify-items-center text-sm text-gray-600">
                                    <label
                                      htmlFor="file-upload"
                                      className="text-center relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none"
                                    >
                                      <span className="text-center">
                                        Upload a file
                                      </span>
                                      <p className="text-xs text-gray-500">
                                        CSV up to 10MB
                                      </p>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </CSVReader>
                    </div>
                  </div>
                </div>
              </div>
              {loading && (
                <div className="w-6 h-6">
                  <CircleLoader />
                </div>
              )}
              {!loading && (
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={!selectedFile}
                    className={`${
                      !selectedFile
                        ? "text-gray-400 bg-gray-100 hover:bg-gray-100 cursor-not-allowed"
                        : "text-white bg-indigo-600 hover:bg-indigo-700 "
                    } ml-4 inline-flex items-center px-4 py-1.5 border border-transparent text-xs 
                  font-medium rounded shadow-sm 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                    onClick={handleImportData}
                  >
                    Upload
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={() => {
                      setSelectedFile(null);
                      setOpen(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
